import CartographerReporter, { THIRTY_SECONDS } from './CartographerReporter';
import { getRelativeMarkerTimings } from '../markerUtils';
import { onVisibilityChange, visibilityState, getWasHiddenOnScriptStart } from '../visibility';
import { PageLoadMetrics } from '../Metrics';
import { getIsAiCopilotEnabled } from '../aiCopilot';
import { cleanseRoute } from '../cleanseRoute';
export default class InAppReporter extends CartographerReporter {
  constructor(options) {
    super(options);
    this.softNavigationContext = options === null || options === void 0 ? void 0 : options.softNavigationContext;
    this.stopped = false;
    this.flushQueueTimeout = undefined;
    this.currentActionStartTimestamp = null;
    this.previousNavigationAction = null;
    this.wasHidden = getWasHiddenOnScriptStart() || visibilityState() === 'hidden';
    this._destroyVisibilityListener = onVisibilityChange(state => {
      if (state === 'hidden' && !this.wasHidden) {
        this.wasHidden = true;
      }
    });
  }
  destroy() {
    super.destroy();
    this._destroyVisibilityListener();
  }
  pushInAppNavigationAction(routeInfo, status, wasHidden, isHidden, duration, timestamp, markers) {
    if (this.previousNavigationAction) {
      this.pushNavigationAction({
        to: routeInfo,
        from: this.previousNavigationAction,
        status,
        wasHidden,
        isHidden,
        duration,
        timestamp,
        markers
      });
    }
    this.previousNavigationAction = routeInfo;
  }
  report(action) {
    if (action.type === 'COMPONENT_RENDERED' || this.isResolved(action.payload.entry.id) || this.stopped) {
      return;
    }
    const isAiCopilotEnabled = String(getIsAiCopilotEnabled());
    switch (action.type) {
      case 'ROUTE_TIMEOUT_EXPIRED':
      case 'ROUTE_FAILED':
        {
          const {
            entry,
            routeSpec
          } = action.payload;
          const {
            pathname,
            checks,
            expiredTimestamp
          } = entry;
          const {
            route,
            error
          } = routeSpec;
          if (!this.wasHidden && this.previousNavigationAction) {
            if (action.type === 'ROUTE_FAILED') {
              var _this$options$isGrani, _this$options$isGrani2;
              PageLoadMetrics.counter('transition-failed', {
                route: cleanseRoute(route),
                isAiCopilotEnabled,
                isGraniteEnabled: String((_this$options$isGrani = this.options.isGraniteEnabled) !== null && _this$options$isGrani !== void 0 ? _this$options$isGrani : false),
                isGraniteRemoteApp: String((_this$options$isGrani2 = this.options.isGraniteRemoteApp) !== null && _this$options$isGrani2 !== void 0 ? _this$options$isGrani2 : false)
              }).increment();
            } else {
              var _this$options$isGrani3, _this$options$isGrani4;
              PageLoadMetrics.counter('transition-timeouts', {
                route: cleanseRoute(route),
                isAiCopilotEnabled,
                isGraniteEnabled: String((_this$options$isGrani3 = this.options.isGraniteEnabled) !== null && _this$options$isGrani3 !== void 0 ? _this$options$isGrani3 : false),
                isGraniteRemoteApp: String((_this$options$isGrani4 = this.options.isGraniteRemoteApp) !== null && _this$options$isGrani4 !== void 0 ? _this$options$isGrani4 : false)
              }).increment();
            }
          }
          const {
            duration,
            timestamp
          } = action.type === 'ROUTE_FAILED' ? this.toTimings(checks, this.currentActionStartTimestamp, error) : {
            duration: expiredTimestamp - this.currentActionStartTimestamp,
            timestamp: expiredTimestamp
          };
          const markerTimings = getRelativeMarkerTimings(checks, this.currentActionStartTimestamp);
          this.pushInAppNavigationAction({
            pathname,
            route,
            scenario: action.type === 'ROUTE_FAILED' && error ? error.join(',') : action.type
          }, 'failure', this.wasHidden, visibilityState() === 'hidden', duration, timestamp, markerTimings);
          break;
        }
      case 'ROUTE_PARTIAL_SUCCESS':
        {
          const {
            entry,
            routeSpec,
            extra
          } = action.payload;
          const {
            pathname,
            checks
          } = entry;
          const {
            route
          } = routeSpec;
          const {
            scenario
          } = extra;
          const markers = routeSpec.partialSuccess[scenario] || [];
          const {
            duration,
            timestamp
          } = this.toTimings(checks, this.currentActionStartTimestamp, markers);
          if (!this.wasHidden && this.previousNavigationAction) {
            var _this$options$isGrani5, _this$options$isGrani6, _this$options$isGrani7, _this$options$isGrani8;
            PageLoadMetrics.timer('transition-succeeded', {
              scenario,
              route: cleanseRoute(route),
              isAiCopilotEnabled,
              isGraniteEnabled: String((_this$options$isGrani5 = this.options.isGraniteEnabled) !== null && _this$options$isGrani5 !== void 0 ? _this$options$isGrani5 : false),
              isGraniteRemoteApp: String((_this$options$isGrani6 = this.options.isGraniteRemoteApp) !== null && _this$options$isGrani6 !== void 0 ? _this$options$isGrani6 : false)
            }).update(duration);
            PageLoadMetrics.timer('transition-partial-success', {
              scenario,
              route: cleanseRoute(route),
              isAiCopilotEnabled,
              isGraniteEnabled: String((_this$options$isGrani7 = this.options.isGraniteEnabled) !== null && _this$options$isGrani7 !== void 0 ? _this$options$isGrani7 : false),
              isGraniteRemoteApp: String((_this$options$isGrani8 = this.options.isGraniteRemoteApp) !== null && _this$options$isGrani8 !== void 0 ? _this$options$isGrani8 : false)
            }).update(duration);
          }
          const markerTimings = getRelativeMarkerTimings(checks, this.currentActionStartTimestamp);
          this.pushInAppNavigationAction({
            pathname,
            route,
            scenario: scenario || markers.join(',')
          }, 'partial_success', this.wasHidden, visibilityState() === 'hidden', duration, timestamp, markerTimings);
          break;
        }
      case 'ROUTE_SUCCEEDED':
        {
          const {
            entry,
            routeSpec,
            extra
          } = action.payload;
          const {
            pathname,
            checks
          } = entry;
          const {
            route
          } = routeSpec;
          const {
            scenario
          } = extra;
          const markers = routeSpec.success[scenario] || '';
          const {
            duration,
            timestamp
          } = this.toTimings(checks, this.currentActionStartTimestamp, markers);
          if (!this.wasHidden && this.previousNavigationAction) {
            var _this$options$isGrani9, _this$options$isGrani10;
            PageLoadMetrics.timer('transition-succeeded', {
              scenario,
              route: cleanseRoute(route),
              isAiCopilotEnabled,
              isGraniteEnabled: String((_this$options$isGrani9 = this.options.isGraniteEnabled) !== null && _this$options$isGrani9 !== void 0 ? _this$options$isGrani9 : false),
              isGraniteRemoteApp: String((_this$options$isGrani10 = this.options.isGraniteRemoteApp) !== null && _this$options$isGrani10 !== void 0 ? _this$options$isGrani10 : false)
            }).update(duration);
          }
          const markerTimings = getRelativeMarkerTimings(checks, this.currentActionStartTimestamp);
          this.pushInAppNavigationAction({
            pathname,
            route,
            scenario: scenario || markers.join(',')
          }, 'success', this.wasHidden, visibilityState() === 'hidden', duration, timestamp, markerTimings);
          break;
        }
      default:
    }
    switch (action.type) {
      case 'ROUTE_UNEXPECTED':
        {
          this.stopped = true;
          break;
        }
      case 'ROUTE_STARTED':
        {
          const {
            entry
          } = action.payload;
          const {
            timestamp
          } = entry;
          this.currentActionStartTimestamp = this.currentActionStartTimestamp || timestamp;
          break;
        }
      case 'ROUTE_ABANDONED':
        {
          this.currentActionStartTimestamp = null;
          break;
        }
      case 'ROUTE_SUCCEEDED':
      case 'ROUTE_PARTIAL_SUCCESS':
      case 'ROUTE_TIMEOUT_EXPIRED':
      case 'ROUTE_FAILED':
        {
          this.currentActionStartTimestamp = null;
          this.markResolved(action.payload.entry.id);
          clearTimeout(this.flushQueueTimeout);
          this.flushQueueTimeout = setTimeout(() => {
            this.flushNavigationQueue();
          }, THIRTY_SECONDS);
          break;
        }
      default:
    }
  }
}